import { useMemo, useState } from "react";
import {
  addDays,
  endOfDay,
  endOfMonth,
  format,
  isSameDay,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { Loader2 } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useSetPageHeader } from "@/lib/page-header";
import { useAgendaNavigation, type ViewMode } from "@/lib/agenda-navigation";
import { useAgendaMutations } from "@/lib/agenda-mutations";
import { useAuth } from "@/lib/auth";
import { useIsMobile } from "@/lib/use-media-query";
import { useSelectedDentists } from "@/lib/selected-dentists";
import { useSelectedServices } from "@/lib/selected-services";
import { minutesToTime, timeToMinutes } from "@/lib/agenda-time";
import { WeekView } from "@/components/agenda/WeekView";
import { MonthView } from "@/components/agenda/MonthView";
import { AgendaSidebar } from "@/components/agenda/AgendaSidebar";
import { AgendaTopbarActions } from "@/components/agenda/AgendaTopbarActions";
import { MobileAgenda } from "@/components/agenda/mobile/MobileAgenda";
import {
  AgendaAppointmentDialog,
  type DialogInitial,
} from "@/components/agenda/AgendaAppointmentDialog";
import { useReagendarPorUrl } from "@/components/agenda/useReagendarPorUrl";
import { useProfissionalPorUrl } from "@/components/agenda/useProfissionalPorUrl";
import { FiltroDeOrganizacoes, useOrganizacoesMarcadas, type OrganizacaoDoFiltro } from "@/components/shared/FiltroDeOrganizacoes";
import { useOwnedOrganizations } from "@/lib/owned-organizations";
import { PatientSummaryDialog, type PatientSummaryTarget } from "@/components/shared/PatientSummaryDialog";
import { ProsthesisOrderDetailDialog } from "@/components/prosthesis/ProsthesisOrderDetailDialog";
import { useTourDaPagina } from "@/components/product-tour/PageTourEntry";
import { VoltarAoMenu } from "@/components/layout/VoltarAoMenu";
import { TituloDaPagina } from "@/components/layout/mobile/TituloDaPagina";
import { AGENDA_TOUR_STEPS } from "@/lib/product-tour/page-tour-steps";
import { consultaParaEdicao } from "@/components/agenda/consulta-para-edicao";
import { useAgendaPorSala } from "@/components/agenda/use-agenda-por-sala";

const LS_SIDEBAR_OPEN = "agenda_sidebarOpen";

type DialogState =
  | { mode: "closed" }
  | { mode: "create"; initial: DialogInitial }
  | { mode: "edit"; initial: DialogInitial };

export function Agenda() {
  const isMobile = useIsMobile();
  const { anchorDate, setAnchorDate, viewMode, changeViewMode, navigate, goToday } =
    useAgendaNavigation();

  const [pickerOpen, setPickerOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(LS_SIDEBAR_OPEN) === "true";
  });
  const [dialog, setDialog] = useState<DialogState>({ mode: "closed" });
  const porSala = useAgendaPorSala(viewMode);

  // Filtro de profissionais — default = user logado (único dentista atual).
  const { user } = useAuth();
  // Default do filtro na primeira visita: a própria agenda, pra quem atende. Quem NÃO atende
  // (recepção, secretaria, administrativo) não tem agenda própria — o default de "só eu" deixava a
  // tela vazia e escondia a consulta que a pessoa acabou de marcar, até ela achar "Selecionar
  // todos". Nesse caso o default é a equipe inteira.
  const dentistasQuery = trpc.organization.listDentists.useQuery(undefined, { staleTime: 60_000 });
  const fallbackDentistIds = useMemo(() => {
    const daOrg = (dentistasQuery.data ?? []) as Array<{ id: string }>;
    if (!user?.id || daOrg.length === 0) return [];
    return daOrg.some((d) => d.id === user.id) ? [user.id] : daOrg.map((d) => d.id);
  }, [user?.id, dentistasQuery.data]);
  const dentistFilter = useSelectedDentists(fallbackDentistIds);
  useProfissionalPorUrl(dentistFilter.setIds);
  const serviceFilter = useSelectedServices();
  const [summaryTarget, setSummaryTarget] = useState<PatientSummaryTarget | null>(null);
  const [orderDetailId, setOrderDetailId] = useState<string | null>(null);

  const isPersonalOrg = !!user?.activeOrganization?.isPersonal;
  const { clinics: ownedClinics } = useOwnedOrganizations();
  const membershipsQuery = trpc.organization.listMine.useQuery();
  const otherMemberships = useMemo(
    () => (membershipsQuery.data ?? []).filter((m) => m.organization.id !== user?.activeOrganization?.id),
    [membershipsQuery.data, user?.activeOrganization?.id],
  );
  const otherOwnedClinics = useMemo(
    () => ownedClinics.filter((o) => o.id !== user?.activeOrganization?.id),
    [ownedClinics, user?.activeOrganization?.id],
  );
  const showScopeFilter = isPersonalOrg && (otherOwnedClinics.length > 0 || otherMemberships.length > 0);
  // Acesso pessoal: quais organizações entram na grade (a própria + as clínicas com vínculo).
  const organizacoesDoFiltro = useMemo<OrganizacaoDoFiltro[]>(
    () => [
      ...(user?.activeOrganization ? [{ id: user.activeOrganization.id, nome: "Pessoal", pessoal: true }] : []),
      ...otherMemberships
        .filter((m) => !m.organization.types?.length || m.organization.types.includes("DENTAL_OFFICE"))
        .map((m) => ({ id: m.organization.id, nome: m.organization.name })),
    ],
    [user?.activeOrganization, otherMemberships],
  );
  const filtroDeOrganizacoes = useOrganizacoesMarcadas("agenda:filtro-de-organizacoes", organizacoesDoFiltro);
  const marcadas = filtroDeOrganizacoes.marcadas;

  const openSidebar = () => {
    setSidebarOpen(true);
    if (typeof window !== "undefined") localStorage.setItem(LS_SIDEBAR_OPEN, "true");
    setPickerOpen(false);
  };
  const closeSidebar = () => {
    setSidebarOpen(false);
    if (typeof window !== "undefined") localStorage.setItem(LS_SIDEBAR_OPEN, "false");
  };

  const settingsQuery = trpc.settings.getAgenda.useQuery();
  const utils = trpc.useUtils();

  // Range pra lookup de appointments por id (pra edit/status/delete)
  const rangeQueryArgs = useMemo(() => {
    if (viewMode === "month") {
      return {
        rangeStart: startOfMonth(anchorDate),
        rangeEnd: endOfMonth(anchorDate),
      };
    }
    const start = startOfWeek(anchorDate, { weekStartsOn: 0 });
    const len = viewMode === "fortnight" ? 14 : viewMode === "day" ? 1 : 7;
    // Dia inteiro nas pontas: a âncora carrega hora, e no modo dia início = fim viraria um instante só.
    return {
      rangeStart: startOfDay(viewMode === "day" ? anchorDate : start),
      rangeEnd: endOfDay(viewMode === "day" ? anchorDate : addDays(start, len - 1)),
    };
  }, [anchorDate, viewMode]);

  const allAppointmentsQuery = trpc.appointment.listByRange.useQuery(rangeQueryArgs, {
    enabled: !!settingsQuery.data,
  });

  const appointmentsById = useMemo(() => {
    const map = new Map<string, any>();
    for (const a of (allAppointmentsQuery.data ?? []) as any[]) map.set(a.id, a);
    return map;
  }, [allAppointmentsQuery.data]);

  // Compromissos de OUTRAS organizações — só pra Agenda Pessoal, mesclados na mesma grade
  // (scope "todas"/"organizacoes"), sempre só-leitura. `listOwnedAcrossOrgs` (OWNER) cobre
  // tudo das organizações possuídas; `listMyAppointmentsAcrossOrgs` (qualquer vínculo) cobre só
  // os próprios compromissos em organizações onde só é membro — dedup abaixo evita mostrar 2x
  // o que já vem pela query de OWNER.
  const needsForeignData = showScopeFilter && organizacoesDoFiltro.some((o) => !o.pessoal && marcadas.has(o.id));
  const ownedAcrossOrgsQuery = trpc.appointment.listOwnedAcrossOrgs.useQuery(rangeQueryArgs, {
    enabled: needsForeignData && otherOwnedClinics.length > 0,
  });
  const myAppointmentsAcrossOrgsQuery = trpc.appointment.listMyAppointmentsAcrossOrgs.useQuery(rangeQueryArgs, {
    enabled: needsForeignData && otherMemberships.length > 0,
  });

  const foreignAppointments = useMemo(() => {
    if (!needsForeignData) return [];
    const ownedOrgIds = new Set(otherOwnedClinics.map((o) => o.id));
    const owned = (ownedAcrossOrgsQuery.data ?? []) as any[];
    const mine = ((myAppointmentsAcrossOrgsQuery.data ?? []) as any[]).filter(
      (a) => !ownedOrgIds.has(a.organization.id),
    );
    return [...owned, ...mine];
  }, [needsForeignData, otherOwnedClinics, ownedAcrossOrgsQuery.data, myAppointmentsAcrossOrgsQuery.data]);

  const foreignAppointmentsById = useMemo(() => {
    const map = new Map<string, any>();
    for (const a of foreignAppointments) map.set(a.id, a);
    return map;
  }, [foreignAppointments]);

  const extraAppointments = useMemo(
    () => foreignAppointments.filter((a) => marcadas.has(a.organization.id)),
    [foreignAppointments, marcadas],
  );
  const includeOwn = !showScopeFilter || marcadas.has(user?.activeOrganization?.id ?? "");

  const {
    handleMove,
    handleStatusChange,
    handleDelete,
    handleIniciarAtendimento,
    handleEncerrarAtendimento,
    atendimentoPendente,
  } = useAgendaMutations({
    settings: settingsQuery.data,
    findAppointment: (id) => appointmentsById.get(id),
  });

  const openEditDialog = (id: string) => {
    const foreign = foreignAppointmentsById.get(id);
    if (foreign) {
      if (foreign.patientId) {
        setSummaryTarget({
          patientId: foreign.patientId,
          organizationId: foreign.organization.id,
          patientName: foreign.patientName,
        });
      }
      return;
    }
    const appt = appointmentsById.get(id);
    if (!appt) return;
    setDialog({ mode: "edit", initial: consultaParaEdicao(appt) });
  };

  // Na agenda por sala, o duplo clique já diz a sala (null = coluna "Sem sala").
  const handleSlotDoubleClick = (date: Date, startTime: string, salaId?: string | null) => {
    if (!settingsQuery.data) return;
    const startMin = timeToMinutes(startTime);
    const endMin = Math.min(
      startMin + settingsQuery.data.defaultAppointmentDuration,
      timeToMinutes(settingsQuery.data.endHour),
    );
    setDialog({
      mode: "create",
      initial: { date, startTime, endTime: minutesToTime(endMin), ...(salaId !== undefined ? { salaId } : {}) },
    });
  };

  const openNewAppointmentNow = () => {
    if (!settingsQuery.data) return;
    const startMin = timeToMinutes(settingsQuery.data.startHour);
    const endMin = Math.min(
      startMin + settingsQuery.data.defaultAppointmentDuration,
      timeToMinutes(settingsQuery.data.endHour),
    );
    setDialog({
      mode: "create",
      initial: {
        date: anchorDate,
        startTime: settingsQuery.data.startHour,
        endTime: minutesToTime(endMin),
      },
    });
  };

  useReagendarPorUrl({
    pronto: !!settingsQuery.data,
    horarioPadrao: {
      date: anchorDate,
      startTime: settingsQuery.data?.startHour ?? "08:00",
      endTime: minutesToTime(
        Math.min(
          timeToMinutes(settingsQuery.data?.startHour ?? "08:00") +
            (settingsQuery.data?.defaultAppointmentDuration ?? 30),
          timeToMinutes(settingsQuery.data?.endHour ?? "18:00"),
        ),
      ),
    },
    aoAbrir: (initial) => setDialog({ mode: "create", initial }),
  });

  const handleSaved = () => utils.appointment.invalidate();

  const formattedDate = format(anchorDate, "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  const capitalizedDate = formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
  const shortDate = format(anchorDate, "EEE, dd 'de' MMM", { locale: ptBR });
  const capShortDate = shortDate.charAt(0).toUpperCase() + shortDate.slice(1);

  useTourDaPagina(AGENDA_TOUR_STEPS);
  useSetPageHeader(
    {
      title: "Agenda",
      subtitle: isMobile ? capShortDate : capitalizedDate,
    },
    [isMobile, capitalizedDate, capShortDate],
  );

  if (settingsQuery.isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
      </div>
    );
  }

  const settings = settingsQuery.data!;
  const today = new Date();
  const todayVisible = isDateVisibleInView(anchorDate, today, viewMode);

  const scopeFilter = showScopeFilter ? (
    <div className="px-4 py-3 sm:px-6 md:px-8">
      <FiltroDeOrganizacoes
        organizacoes={organizacoesDoFiltro}
        {...filtroDeOrganizacoes.controle}
      />
    </div>
  ) : null;

  if (isMobile) {
    const mobileViewMode = viewMode === "fortnight" ? "week" : viewMode;
    return (
      // Tela cheia entre a barra de cima e a barra de abas. O `<main>` reserva embaixo a folga da
      // barra mais 1,75rem de respiro; a margem negativa devolve esse respiro pra agenda encostar nela.
      <div
        className="-mx-4 -mt-4 flex flex-col"
        style={{
          height: "calc(100dvh - 3.75rem - env(safe-area-inset-top) - 3.75rem - env(safe-area-inset-bottom))",
          marginBottom: "-1.75rem",
        }}
      >
        {/* Nome da página com respiro embaixo: colado na faixa azul do período, parecia torto. */}
        <TituloDaPagina className="shrink-0 px-4 pb-3 pt-3" />
        {scopeFilter}
        <div className="min-h-0 flex-1">
          <MobileAgenda
            anchorDate={anchorDate}
            viewMode={mobileViewMode}
            settings={settings}
            changeViewMode={changeViewMode}
            navigate={navigate}
            extraAppointments={extraAppointments}
            includeOwn={includeOwn}
            onAppointmentClick={openEditDialog}
            onNewAppointment={openNewAppointmentNow}
          />
        </div>
        {dialog.mode !== "closed" && (
          <AgendaAppointmentDialog
            open
            mode={dialog.mode}
            initial={dialog.initial}
            settings={settings}
            onClose={() => setDialog({ mode: "closed" })}
            onSaved={handleSaved}
          />
        )}
        <PatientSummaryDialog target={summaryTarget} onClose={() => setSummaryTarget(null)} />
        <ProsthesisOrderDetailDialog orderId={orderDetailId} onClose={() => setOrderDetailId(null)} />
      </div>
    );
  }

  return (
    <div className="-m-4 flex h-[calc(100vh-4rem)] flex-col sm:-m-6 md:-m-8">
      {/* Os controles moravam no título da barra superior. Com a barra reservada a switcher, sino
          e perfil, viram a faixa de cima da própria agenda — colada à grade que eles comandam. */}
      <div className="flex shrink-0 items-center border-b border-zinc-200 bg-white px-4 py-2 sm:px-6 md:px-8 dark:border-border dark:bg-card">
        <VoltarAoMenu className="mr-4 shrink-0" />
        <AgendaTopbarActions
          anchorDate={anchorDate}
          viewMode={viewMode}
          changeViewMode={changeViewMode}
          navigate={navigate}
          goToday={goToday}
          onNewAppointment={openNewAppointmentNow}
          sidebarOpen={sidebarOpen}
          openSidebar={openSidebar}
          pickerOpen={pickerOpen}
          setPickerOpen={setPickerOpen}
          onSelectDate={setAnchorDate}
          isMobile={false}
          dentistFilter={dentistFilter}
          dentistSettings={settings.dentistSettings}
          serviceFilter={serviceFilter}
          porSala={porSala.disponivel ? porSala : null}
        />
      </div>
      {scopeFilter}
      <div className="flex min-h-0 flex-1">
        {sidebarOpen && (
          <AgendaSidebar
            selectedDate={anchorDate}
            onSelectDate={setAnchorDate}
            onClose={closeSidebar}
            dentistFilter={dentistFilter}
            dentistSettings={settings.dentistSettings}
            serviceFilter={serviceFilter}
          />
        )}

        <div className="min-w-0 flex-1 overflow-hidden">
          {viewMode === "month" ? (
            <MonthView
              anchorDate={anchorDate}
              settings={settings}
              extraAppointments={extraAppointments}
              includeOwn={includeOwn}
              onAppointmentEdit={openEditDialog}
              onOrderClick={setOrderDetailId}
              onDayClick={(d) => {
                setAnchorDate(d);
                changeViewMode("day");
              }}
            />
          ) : (
            <WeekView
              anchorDate={anchorDate}
              settings={settings}
              viewMode={viewMode}
              selectedDentistIds={dentistFilter.ids}
              selectedServiceIds={serviceFilter.ids}
              extraAppointments={extraAppointments}
              includeOwn={includeOwn}
              onAppointmentEdit={openEditDialog}
              onAppointmentStatusChange={handleStatusChange}
              onAppointmentIniciarAtendimento={handleIniciarAtendimento}
              onAppointmentEncerrarAtendimento={handleEncerrarAtendimento}
              atendimentoPendente={atendimentoPendente}
              onAppointmentDelete={handleDelete}
              onSlotDoubleClick={handleSlotDoubleClick}
              onAppointmentMove={handleMove}
              onOrderClick={setOrderDetailId}
              salas={porSala.salas}
            />
          )}

          {!todayVisible && (
            <div className="fixed bottom-6 left-1/2 z-40 -translate-x-1/2">
              <button
                type="button"
                onClick={goToday}
                className="inline-flex h-9 items-center gap-2 rounded-full bg-brand-600 px-4 text-xs font-medium text-white shadow-lg transition-transform hover:scale-105 hover:bg-brand-700"
              >
                Voltar para hoje
              </button>
            </div>
          )}
        </div>
      </div>

      <PatientSummaryDialog target={summaryTarget} onClose={() => setSummaryTarget(null)} />
      <ProsthesisOrderDetailDialog orderId={orderDetailId} onClose={() => setOrderDetailId(null)} />

      {dialog.mode !== "closed" && (
        <AgendaAppointmentDialog
          open
          mode={dialog.mode}
          initial={dialog.initial}
          settings={settings}
          onClose={() => setDialog({ mode: "closed" })}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

function isDateVisibleInView(anchor: Date, target: Date, mode: ViewMode): boolean {
  if (mode === "day") return isSameDay(anchor, target);
  if (mode === "month") {
    return (
      anchor.getMonth() === target.getMonth() &&
      anchor.getFullYear() === target.getFullYear()
    );
  }
  const start = startOfWeek(anchor, { weekStartsOn: 0 });
  const len = mode === "fortnight" ? 14 : 7;
  for (let i = 0; i < len; i++) {
    if (isSameDay(addDays(start, i), target)) return true;
  }
  return false;
}
